import { useChapters, useGlossary } from "@/shared/api/book";
import { num, useFilters } from "@/shared/lib/use-filters";
import { Chip } from "@/shared/ui/chip";
import { SearchInput, Select } from "@/shared/ui/filters";
import { Markdown } from "@/shared/ui/markdown";
import { PageTitle } from "@/shared/ui/page-header";
import { Empty, ErrorNote, ListSkeleton, Pager } from "@/shared/ui/state";

const DEFAULTS = { q: "", chapter_id: "", page: "1" };

export function GlossaryPage() {
  const { values, set } = useFilters(DEFAULTS);
  const { data: chapters } = useChapters();
  const { data, isPending, isError } = useGlossary({
    q: values.q,
    chapter_id: num(values.chapter_id),
    page: Number(values.page) || 1,
  });

  return (
    <div className="flex flex-col gap-4">
      <PageTitle>Fogalomtár</PageTitle>

      <SearchInput
        label="Keresés a fogalmak között"
        placeholder="Keresés a fogalmak között"
        value={values.q}
        onChange={(q) => set({ q })}
      />

      {chapters && chapters.length > 0 && (
        <Select
          aria-label="Fejezet"
          value={values.chapter_id}
          onChange={(e) => set({ chapter_id: e.target.value })}
        >
          <option value="">Minden fejezet</option>
          {chapters.flatMap((c) => [c, ...c.children]).map((c) => (
            <option key={c.id} value={c.id}>
              {c.number}. {c.title}
            </option>
          ))}
        </Select>
      )}

      {isPending && <ListSkeleton />}
      {isError && <ErrorNote />}
      {data &&
        (data.items.length === 0 ? (
          <Empty>
            {values.q ? "Erre a keresésre nincs találat." : "A fogalomtár még nincs feltöltve."}
          </Empty>
        ) : (
          <dl className="flex flex-col">
            {data.items.map((g) => (
              <div key={g.id} className="flex flex-col gap-1.5 border-t border-line py-3">
                {/* A könyv fogalomtára az angol terminusra épül: az van elöl, az adja a sorrendet. */}
                <dt className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-[16px] font-semibold">{g.term_en ?? g.terms_hu}</span>
                  {g.term_en && <span className="text-[14px] text-ink-soft">{g.terms_hu}</span>}
                </dt>
                {/* A leírás dőlttel — így ránézésre elválik a magyar megnevezéstől. */}
                {g.body && (
                  <dd>
                    <Markdown className="text-[14px] italic">{g.body}</Markdown>
                  </dd>
                )}
                {/* A fejezet-jelölés másodlagos információ: halk szürke, nem arany. */}
                {g.chapters.length > 0 && (
                  <dd className="flex flex-wrap items-center gap-1.5">
                    {g.chapters.map((c) => (
                      <Chip key={c.id} variant="subtle">
                        {c.number}
                      </Chip>
                    ))}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        ))}
      {data && (
        <Pager
          page={data.page}
          total={data.total}
          pageSize={data.page_size}
          onChange={(page) => set({ page: String(page) })}
        />
      )}
    </div>
  );
}
